import { useForm } from 'react-hook-form'
import { useModal } from '../../contexts/ModalContext'
import { useSaveUser } from '../../hooks/user/useSaveUser'
import { useUploadProfilePhoto } from '../../hooks/user/useUploadProfilePhoto'
import type User from '../../model/User'
import { logError } from '../../util/errorLogger'
import Button from '../primitives/Button'
import TextField from '../primitives/TextField'
import ProfileHero from './ProfileHero'
import RoadTripSnackSelector from './RoadTripSnackSelector'
import styles from './ProfileScreen.module.css'

interface ProfileForm {
  firstName: string
  lastName: string
  bio: string
  photoUrl: string
  whyChallenge: string
  hypeSong: string
  roadTripSnacks: string[]
  homeBase: string
}

interface ProfileEditFormProps {
  user: User
  challengeName: string
  onDone: () => void
}

export default function ProfileEditForm({ user, challengeName, onDone }: ProfileEditFormProps) {
  const { showSnackbar } = useModal()
  const saveUser = useSaveUser()
  const uploadPhoto = useUploadProfilePhoto(user.id)
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProfileForm>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      bio: user.bio ?? '',
      photoUrl: user.photoUrl ?? '',
      whyChallenge: user.whyChallenge ?? '',
      hypeSong: user.hypeSong ?? '',
      roadTripSnacks: user.getRoadTripSnacksList(),
      homeBase: user.homeBase ?? '',
    },
  })
  const values = watch()

  function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    uploadPhoto.mutate(file, {
      onSuccess: (photoUrl) => setValue('photoUrl', photoUrl, { shouldDirty: true }),
      onError: (error) => {
        void logError(error, { screen: 'Profile', action: 'uploadImage' })
        showSnackbar('upload-error', 'Sorry! There was a problem uploading your photo.', 'error')
      },
    })
  }

  async function onSubmit(data: ProfileForm) {
    try {
      await saveUser.mutateAsync(
        user
          .updateFirstName(data.firstName)
          .updateLastName(data.lastName)
          .updateBio(data.bio)
          .updatePhotoUrl(data.photoUrl)
          .updateWhyChallenge(data.whyChallenge)
          .updateHypeSong(data.hypeSong)
          .updateRoadTripSnacks(data.roadTripSnacks.join(','))
          .updateHomeBase(data.homeBase),
      )
      showSnackbar('profile-success', 'Profile updated', 'success')
      onDone()
    } catch (error) {
      void logError(error, { screen: 'Profile', action: 'saveProfile' })
      showSnackbar('profile-error', 'Sorry! There was a problem saving your profile.', 'error')
    }
  }

  return (
    <>
      <ProfileHero
        firstName={values.firstName}
        lastName={values.lastName}
        photoUrl={values.photoUrl}
        isUploading={uploadPhoto.isPending}
      >
        <h2 className={styles.name}>Edit profile</h2>
        <label className={styles.textLink}>
          {values.photoUrl ? 'Change photo' : 'Upload photo'}
          <input
            type="file"
            className={styles.fileInput}
            accept="image/*"
            onChange={handlePhotoChange}
          />
        </label>
      </ProfileHero>

      <form className={styles.editForm} onSubmit={handleSubmit(onSubmit)}>
        <TextField
          label="First name"
          error={errors.firstName?.message}
          {...register('firstName', { required: 'First name is required' })}
        />
        <TextField
          label="Last name"
          error={errors.lastName?.message}
          {...register('lastName', { required: 'Last name is required' })}
        />
        <TextField label="Home base (city, state)" {...register('homeBase')} />
        <TextField
          label="7 word bio"
          placeholder="7 words to describe yourself to your team"
          error={errors.bio?.message}
          {...register('bio', {
            validate: (value) =>
              value.split(' ').filter(Boolean).length <= 7 || 'Bio must be 7 words or less',
          })}
        />
        <TextField label={`Why'd ya say yes to ${challengeName}?`} {...register('whyChallenge')} />
        <TextField label="Hype song" {...register('hypeSong')} />

        <div className={styles.field}>
          <h3>Fave road trip snacks</h3>
          <RoadTripSnackSelector
            selectedSnacks={values.roadTripSnacks}
            onChange={(snacks) => setValue('roadTripSnacks', snacks, { shouldDirty: true })}
          />
        </div>

        <div className={styles.formActions}>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isDirty || saveUser.isPending || uploadPhoto.isPending}
          >
            {saveUser.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </>
  )
}
